// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Stepper,
  Button,
  Group,
  Paper,
  Text,
  Switch,
  SimpleGrid,
  Alert,
  Radio,
  Loader,
  Table,
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconCalendar,
  IconSettings,
  IconEye,
  IconCheck,
  IconAlertTriangle,
  IconAlertCircle,
  IconBuildingBank,
  IconFileInvoice,
  IconArrowRight,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { usePayPeriods, useRunPayroll } from '../../hooks/usePayroll';
import { formatDate } from '../../utils/format';
import type { PayPeriod } from '../../types/payroll';

export function PayrollRunPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [isDryRun, setIsDryRun] = useState(true);
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const { data: payPeriodsData, loading: periodsLoading } = usePayPeriods();
  const runPayroll = useRunPayroll();
  const [lastRunResult, setLastRunResult] = useState<unknown>(undefined);

  const payPeriods = useMemo(() => {
    if (!payPeriodsData) return [];
    return payPeriodsData as PayPeriod[];
  }, [payPeriodsData]);

  const openPeriods = useMemo(
    () => payPeriods.filter((p) => p.status === 'open'),
    [payPeriods],
  );

  const selectedPeriod = useMemo(
    () => payPeriods.find((p) => p.id === selectedPeriodId) ?? null,
    [payPeriods, selectedPeriodId],
  );

  const dryRunResult = lastRunResult as
    | {
        runPayroll: {
          runId: string;
          status: string;
          totalEmployees: number;
          totalGross: number;
          totalNet: number;
          isDryRun: boolean;
          errors: string[];
          warnings: string[];
        };
      }
    | undefined;

  const dryRunPayload = dryRunResult?.runPayroll;

  const handleRunDryRun = async () => {
    if (!selectedPeriodId) return;
    const result = await runPayroll.mutateAsync({
      payPeriodId: selectedPeriodId,
      isDryRun: true,
      idempotencyKey: `dry-${idempotencyKey}`,
    });
    setLastRunResult(result);
    setActive(2);
  };

  const handleSubmitPayroll = async () => {
    if (!selectedPeriodId) return;
    await runPayroll.mutateAsync({
      payPeriodId: selectedPeriodId,
      isDryRun: false,
      idempotencyKey,
    });
    setActive(3);
  };

  const nextStep = () => {
    if (active === 1) {
      if (isDryRun) {
        handleRunDryRun();
        return;
      }
      setActive(2);
      return;
    }
    setActive((c) => Math.min(c + 1, 3));
  };

  const prevStep = () => setActive((c) => Math.max(c - 1, 0));

  const canProceed = (): boolean => {
    switch (active) {
      case 0:
        return selectedPeriodId !== null;
      case 1:
        return true;
      case 2:
        return true;
      case 3:
        return false;
      default:
        return false;
    }
  };

  return (
    <Stack>
      <PageHeader
        title="Run Payroll"
        breadcrumbs={[
          { label: 'Payroll', to: '/admin/workforce/payroll' },
          { label: 'Run Payroll' },
        ]}
      />

      {runPayroll.isPending && (
        <LoadingOverlay message="Processing payroll..." />
      )}

      <Paper withBorder p="xl" radius="md">
        <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
          <Stepper.Step
            label="Select Pay Period"
            description="Choose an open period"
            icon={<IconCalendar size={18} />}
          >
            <Stack gap="md" mt="md">
              <Text fw={500} size="lg">
                Select a pay period to process
              </Text>

              {periodsLoading ? (
                <Group justify="center" py="xl">
                  <Loader />
                </Group>
              ) : openPeriods.length === 0 ? (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="No Open Periods"
                  color="yellow"
                >
                  There are no open pay periods available for processing. Please
                  create or unlock a pay period first.
                </Alert>
              ) : (
                <Radio.Group
                  value={selectedPeriodId ?? ''}
                  onChange={(val) => setSelectedPeriodId(val)}
                >
                  <Stack gap="sm">
                    {openPeriods.map((period) => (
                      <Paper
                        key={period.id}
                        withBorder
                        p="md"
                        radius="md"
                        style={{
                          cursor: 'pointer',
                          borderColor:
                            selectedPeriodId === period.id
                              ? 'var(--mantine-color-blue-6)'
                              : undefined,
                        }}
                        onClick={() => setSelectedPeriodId(period.id)}
                      >
                        <Group justify="space-between">
                          <Group>
                            <Radio value={period.id} />
                            <div>
                              <Text fw={500}>
                                {formatDate(period.startDate)} &ndash;{' '}
                                {formatDate(period.endDate)}
                              </Text>
                              <Text size="sm" c="dimmed">
                                Pay date: {formatDate(period.payDate || period.endDate)}
                              </Text>
                            </div>
                          </Group>
                          <StatusBadge status={period.status} />
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Radio.Group>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step
            label="Configuration"
            description="Run options"
            icon={<IconSettings size={18} />}
          >
            <Stack gap="md" mt="md">
              <Text fw={500} size="lg">
                Payroll run configuration
              </Text>

              {selectedPeriod && (
                <Paper withBorder p="md" radius="md" bg="gray.0">
                  <Text size="sm" c="dimmed">
                    Selected period
                  </Text>
                  <Text fw={500}>
                    {formatDate(selectedPeriod.startDate)} &ndash;{' '}
                    {formatDate(selectedPeriod.endDate)}
                  </Text>
                </Paper>
              )}

              <Paper withBorder p="md" radius="md">
                <Group justify="space-between">
                  <div>
                    <Text fw={500}>Dry Run (Preview)</Text>
                    <Text size="sm" c="dimmed">
                      Run a preview calculation without committing. Recommended
                      to review totals and check for anomalies before submitting.
                    </Text>
                  </div>
                  <Switch
                    checked={isDryRun}
                    onChange={(e) => setIsDryRun(e.currentTarget.checked)}
                    size="lg"
                  />
                </Group>
              </Paper>
            </Stack>
          </Stepper.Step>

          <Stepper.Step
            label="Preview Results"
            description="Review before submit"
            icon={<IconEye size={18} />}
          >
            <Stack gap="md" mt="md">
              <Text fw={500} size="lg">
                Payroll preview results
              </Text>

              {dryRunPayload ? (
                <>
                  <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
                    <StatCard
                      title="Employees"
                      value={dryRunPayload.totalEmployees}
                      icon={<IconCheck size={24} />}
                      color="blue"
                    />
                    <StatCard
                      title="Total Gross"
                      value={new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(dryRunPayload.totalGross)}
                      icon={<IconCheck size={24} />}
                      color="green"
                    />
                    <StatCard
                      title="Total Net"
                      value={new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(dryRunPayload.totalNet)}
                      icon={<IconCheck size={24} />}
                      color="teal"
                    />
                    <StatCard
                      title="Status"
                      value={dryRunPayload.status}
                      icon={<IconCheck size={24} />}
                      color="gray"
                    />
                  </SimpleGrid>

                  {dryRunPayload.warnings.length > 0 && (
                    <Alert
                      icon={<IconAlertTriangle size={16} />}
                      title="Anomaly Warnings"
                      color="yellow"
                    >
                      <Stack gap="xs">
                        {dryRunPayload.warnings.map((w, i) => (
                          <Group key={i} gap="xs">
                            <StatusBadge status="warning" />
                            <Text size="sm">{w}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Alert>
                  )}

                  {dryRunPayload.errors.length > 0 && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      title="Errors"
                      color="red"
                    >
                      <Stack gap="xs">
                        {dryRunPayload.errors.map((e, i) => (
                          <Group key={i} gap="xs">
                            <StatusBadge status="critical" />
                            <Text size="sm">{e}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert icon={<IconAlertCircle size={16} />} color="blue">
                  No preview data available. Go back and run a dry run first, or
                  proceed to submit directly.
                </Alert>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step
            label="Confirm & Submit"
            description="Finalize payroll"
            icon={<IconCheck size={18} />}
          >
            <Stack gap="md" mt="md">
              <Text fw={500} size="lg">
                Payroll submitted successfully
              </Text>

              {dryRunPayload && (
                <Paper withBorder p="lg" radius="md">
                  <Table>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td fw={500}>Run ID</Table.Td>
                        <Table.Td>{dryRunPayload.runId}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Status</Table.Td>
                        <Table.Td>
                          <StatusBadge status={dryRunPayload.status} />
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Employees</Table.Td>
                        <Table.Td>{dryRunPayload.totalEmployees}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Total Gross</Table.Td>
                        <Table.Td>
                          <CurrencyDisplay amount={dryRunPayload.totalGross} />
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td fw={500}>Total Net</Table.Td>
                        <Table.Td>
                          <CurrencyDisplay amount={dryRunPayload.totalNet} />
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}

              {/* Post-submission automation */}
              <Paper withBorder p="md" radius="md" bg="var(--mantine-color-green-0)">
                <Text fw={600} mb="sm">What happens next:</Text>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                    <Text size="sm">Pay stubs generated for all employees</Text>
                  </Group>
                  <Group gap="xs">
                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                    <Text size="sm">Tax withholdings calculated and recorded</Text>
                  </Group>
                  <Group gap="xs">
                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                    <Text size="sm">YTD balances updated</Text>
                  </Group>
                  <Group gap="xs">
                    <IconArrowRight size={16} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>Ready for direct deposit initiation</Text>
                  </Group>
                </Stack>
              </Paper>

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <Button
                  variant="filled"
                  color="blue"
                  icon={<IconBuildingBank size={16} />}
                  size="md"
                  onClick={() => {
                    // In production: calls initiatePayrollDeposit mutation
                    notifications.show({
                      title: 'Deposit Initiated',
                      message: 'ACH direct deposits queued for processing',
                      color: 'green',
                    });
                  }}
                >
                  Initiate Direct Deposits
                </Button>
                <Button
                  variant="light"
                  icon={<IconFileInvoice size={16} />}
                  size="md"
                  onClick={() => navigate('/admin/workforce/payroll/tax-filing')}
                >
                  Tax Filing Center
                </Button>
                <Button
                  variant="light"
                  size="md"
                  onClick={() => navigate('/admin/workforce/payroll')}
                >
                  View Payroll Runs
                </Button>
              </SimpleGrid>
            </Stack>
          </Stepper.Step>
        </Stepper>

        {active < 3 && (
          <Group justify="space-between" mt="xl">
            <Button variant="default" onClick={prevStep} disabled={active === 0}>
              Back
            </Button>
            <Group gap="sm">
              {active === 2 && (
                <Button
                  icon={<IconPlayerPlay size={16} />}
                  onClick={handleSubmitPayroll}
                  loading={runPayroll.isPending}
                  color="green"
                >
                  Submit Payroll
                </Button>
              )}
              {active < 2 && (
                <Button onClick={nextStep} disabled={!canProceed()}>
                  {active === 1 && isDryRun ? 'Run Preview' : 'Next'}
                </Button>
              )}
            </Group>
          </Group>
        )}

        {runPayroll.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Payroll Error"
            color="red"
            mt="md"
          >
            {runPayroll.error.message}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
}
